// © 2026 Buddy Media LLC. All rights reserved.
//
// In-memory bearer storage. Per the T-602 spec we DO NOT persist the
// bearer to localStorage — closed tab = re-auth, by design (no JWT
// stealable from disk). Phase 1 ships a paste-token entry path
// (operator pastes the worker bearer / Anthropic OAuth token); the
// proper Anthropic-OAuth-button flow is decided in T-602-marc and
// lands in a Phase 2/3 PR.

import { create } from "zustand";

interface AuthState {
  bearer: string | null;
  setBearer: (b: string | null) => void;
}

// Hydrate from a same-tab E2E entrypoint: tests / smoke runs can do
// `window.__bw_inject_bearer__("...")` before navigating to a protected
// route to bypass the paste-form. Off in production (the entry point
// is dev/sessionStorage-only and trivial to remove if we ever wire
// real CSP-locked auth).
const initial = (() => {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem("bw_token");
})();

export const useAuthStore = create<AuthState>((set) => ({
  bearer: initial,
  setBearer: (b) => {
    if (typeof window !== "undefined") {
      if (b) window.sessionStorage.setItem("bw_token", b);
      else window.sessionStorage.removeItem("bw_token");
    }
    set({ bearer: b });
  },
}));

if (typeof window !== "undefined") {
  (window as unknown as { __bw_inject_bearer__?: (b: string) => void }).__bw_inject_bearer__ =
    (b: string) => {
      window.sessionStorage.setItem("bw_token", b);
      useAuthStore.setState({ bearer: b });
    };
}
